import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

const CONNECTOR_TYPES = [
  { value: 'Alpaca', label: 'Alpaca' },
];

const CONNECTOR_MODES = [
  { value: 'Paper', label: 'Paper' },
  { value: 'Live', label: 'Live' },
];

export const TradingConnectors = () => {
  const { apiClient } = useAuth();
  const [connectors, setConnectors] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  // Modal/edit state
  const [editingConnector, setEditingConnector] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editType, setEditType] = useState('Alpaca');
  const [editMode, setEditMode] = useState('Paper');
  const [editName, setEditName] = useState('');
  const [editKeyId, setEditKeyId] = useState('');
  const [editSecretKey, setEditSecretKey] = useState('');
  const [saving, setSaving] = useState(false);

  const loadConnectors = useCallback(async () => {
    setLoadError('');
    try {
      const data = await apiClient.getTradingConnectors();
      setConnectors(data || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load trading connectors.');
    }
  }, [apiClient]);

  useEffect(() => { loadConnectors(); }, [loadConnectors]);

  const openCreateModal = () => {
    setEditingConnector(null);
    setIsCreating(true);
    setEditType('Alpaca');
    setEditMode('Paper');
    setEditName('');
    setEditKeyId('');
    setEditSecretKey('');
    setSaveError('');
  };

  const openEditModal = (connector) => {
    setEditingConnector(connector);
    setIsCreating(false);
    setEditType(connector.Type || 'Alpaca');
    setEditMode(connector.Mode || 'Paper');
    setEditName(connector.Name || '');
    setEditKeyId(''); // never pre-fill; user enters a new value to replace
    setEditSecretKey(''); // never pre-fill; user enters a new value to replace
    setSaveError('');
  };

  const closeModal = () => {
    if (saving) return;
    setEditingConnector(null);
    setIsCreating(false);
    setSaveError('');
  };

  const handleSave = async (evt) => {
    evt.preventDefault();
    setSaveError('');

    if (!editName.trim()) {
      setSaveError('Name is required.');
      return;
    }

    if (isCreating) {
      if (!editKeyId.trim()) {
        setSaveError('KeyId is required when creating a connector.');
        return;
      }
      if (!editSecretKey.trim()) {
        setSaveError('SecretKey is required when creating a connector.');
        return;
      }
    }

    const body = {
      Name: editName.trim(),
      Type: editType,
      Mode: editMode,
    };

    if (isCreating) {
      body.KeyId = editKeyId.trim();
      body.SecretKey = editSecretKey.trim();
    } else {
      if (editKeyId.trim()) {
        body.KeyId = editKeyId.trim();
      }
      if (editSecretKey.trim()) {
        body.SecretKey = editSecretKey.trim();
      }
    }

    if (!isCreating && editingConnector) {
      body.Id = editingConnector.Id;
    }

    setSaving(true);
    try {
      const saved = await apiClient.saveTradingConnector(body);
      if (isCreating) {
        setConnectors((prev) => [saved, ...(prev || [])]);
      } else {
        setConnectors((prev) => (prev || []).map((c) => (c.Id === saved.Id ? saved : c)));
      }
      closeModal();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save trading connector.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (connector) => {
    if (!window.confirm(`Delete the trading connector "${connector.Type} / ${connector.Mode}"?`)) {
      return;
    }
    try {
      await apiClient.deleteTradingConnector(connector.Id);
      setConnectors((prev) => (prev || []).filter((c) => c.Id !== connector.Id));
    } catch (err) {
      alert('Failed to delete connector: ' + (err?.message || err));
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
        <h1 className="mb-0">Trading Connectors</h1>
        <Button color="success" size="sm" onClick={openCreateModal}>
          Create Connector
        </Button>
      </div>

      {loadError && <Alert color="danger">{loadError}</Alert>}

      {!connectors && !loadError && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {connectors && connectors.length === 0 && (
        <p className="text-muted mb-0">No trading connectors yet. Click "Create Connector" to add one.</p>
      )}

      {connectors && connectors.length > 0 && (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th>Type</th>
              <th>Mode</th>
              <th>Created</th>
              <th>Updated</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {connectors.map((c) => (
              <tr key={c.Id}>
                <td>{c.Id}</td>
                <td>{c.Name}</td>
                <td>{c.getTypeDisplay()}</td>
                <td>{c.getModeDisplay()}</td>
                <td>{c.getFormattedCreateTime()}</td>
                <td>{c.getFormattedUpdateTime()}</td>
                <td>
                  <div className="d-flex" style={{ gap: '8px' }}>
                    <Button color="primary" size="sm" onClick={() => openEditModal(c)}>
                      Edit
                    </Button>
                    <Button color="danger" size="sm" onClick={() => handleDelete(c)}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={isCreating || !!editingConnector} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>
          {isCreating ? 'Create Trading Connector' : `Edit Trading Connector #${editingConnector?.Id}`}
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {saveError && <Alert color="danger">{saveError}</Alert>}

            <FormGroup>
              <Label for="connectorType">Type</Label>
              <Input
                id="connectorType"
                type="select"
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
                disabled={saving}
              >
                {CONNECTOR_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="connectorMode">Mode</Label>
              <Input
                id="connectorMode"
                type="select"
                value={editMode}
                onChange={(e) => setEditMode(e.target.value)}
                disabled={saving}
              >
                {CONNECTOR_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="connectorName">Name</Label>
              <Input
                id="connectorName"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={saving}
                maxLength={128}
                placeholder="e.g. My Alpaca Paper Account"
              />
            </FormGroup>

            {editType === 'Alpaca' && (
              <>
                <FormGroup>
                  <Label for="connectorKeyId">
                    KeyId{!isCreating && <span className="text-muted"> (leave blank to keep current)</span>}
                  </Label>
                  <Input
                    id="connectorKeyId"
                    type="text"
                    value={editKeyId}
                    onChange={(e) => setEditKeyId(e.target.value)}
                    disabled={saving}
                    autoComplete="off"
                    placeholder={isCreating ? 'Enter Alpaca KeyId' : '••••••'}
                  />
                  {!isCreating && editingConnector?.HasKeyId && (
                    <small className="form-text text-muted">
                      Current KeyId: <code>{editingConnector.KeyIdMask}</code>
                    </small>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label for="connectorSecretKey">
                    SecretKey{!isCreating && <span className="text-muted"> (leave blank to keep current)</span>}
                  </Label>
                  <Input
                    id="connectorSecretKey"
                    type="password"
                    value={editSecretKey}
                    onChange={(e) => setEditSecretKey(e.target.value)}
                    disabled={saving}
                    autoComplete="off"
                    placeholder={isCreating ? 'Enter Alpaca SecretKey' : '••••••'}
                  />
                  {!isCreating && editingConnector?.HasSecretKey && (
                    <small className="form-text text-muted">
                      Current SecretKey: <code>{editingConnector.SecretKeyMask}</code>
                    </small>
                  )}
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button
              color="primary"
              type="submit"
              disabled={
                saving ||
                (isCreating && (
                  !editKeyId.trim() ||
                  (editType === 'Alpaca' && !editSecretKey.trim())
                ))
              }
            >
              {saving ? <Spinner size="sm" /> : (isCreating ? 'Create' : 'Save')}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};
